import { useSelector } from '@xstate/react';
import { createEmptyActor } from 'xstate';
import { UserContext } from '~/app';

const emptyActor = createEmptyActor();

/** The name on a provider's MCV profile (`mcv.providers`), as Account settings shows it. */
export interface ProviderAccountName {
  firstName: string;
  lastName: string;
}

/** The Clerk user the user machine has loaded, which lags Clerk itself when accounts switch. */
export const useSessionClerkUserId = (): string | undefined =>
  UserContext.useSelector((state) => state.context.userId);

/**
 * [MCV-1834] The name Account settings shows for the signed-in user.
 *
 * - `undefined`: not a provider (employers and admins keep the Clerk name).
 * - `null`: a provider whose profile has not loaded yet.
 * - otherwise the MCV profile's first and last name, the same record the side nav reads.
 */
export const useProviderAccountName = (): ProviderAccountName | null | undefined => {
  const isProvider = UserContext.useSelector((state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_PROVIDER' })
  );
  const vaultRef = UserContext.useSelector((state) => state.context.vaultRef);
  const provider = useSelector(
    vaultRef ?? emptyActor,
    (state) => (state as { context?: { provider?: ProviderAccountName } }).context?.provider
  );

  if (!isProvider) return undefined;
  if (!provider) return null;
  return { firstName: provider.firstName ?? '', lastName: provider.lastName ?? '' };
};
